import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  ArrowLeft, 
  Image, 
  Video, 
  Music, 
  FileText, 
  Download, 
  Play, 
  Clock, 
  Users,
  CheckCircle,
  ArrowRight,
  Sparkles,
  X,
  Eye
} from "lucide-react";
import { format } from "date-fns";

const committeeData = {
  "mosque-dawah": {
    name: "Mosque and Da'wah",
    tagline: "Spiritual Guidance & Islamic Education",
    description: "The Mosque and Da'wah committee is responsible for the management of the mosque and Islamic outreach programs. We organize religious education, community guidance, Quranic studies, and Friday sermons.",
    icon: "🕌",
    color: "from-emerald-500 to-teal-600",
    bgColor: "bg-emerald-50",
    textColor: "text-emerald-700",
    hasMediaGallery: true,
    activities: [
      "Daily prayer management and coordination",
      "Friday Jumu'ah sermon organization",
      "Islamic education classes for all ages",
      "Da'wah outreach programs",
      "Ramadan activities and Taraweeh",
      "Youth Islamic programs and mentorship"
    ]
  },
  "project-maintenance": {
    name: "Project and Maintenance",
    tagline: "Building & Infrastructure Excellence",
    description: "The Project and Maintenance committee handles all construction, renovation, and maintenance projects to ensure our facilities serve the community effectively. We oversee building projects, repairs, and infrastructure development.",
    icon: "🔧",
    color: "from-blue-500 to-indigo-600",
    bgColor: "bg-blue-50",
    textColor: "text-blue-700",
    hasMediaGallery: false,
    activities: [
      "Building maintenance and repairs",
      "Infrastructure development projects",
      "Renovation and expansion works",
      "Equipment management and procurement",
      "Facility upgrades and modernization",
      "Regular safety inspections"
    ]
  },
  "welfare-publicity": {
    name: "Welfare and Publicity",
    tagline: "Community Outreach & Support",
    description: "The Welfare and Publicity committee manages welfare programs for the less privileged and handles public relations. We coordinate charity distributions, support programs, and community outreach initiatives.",
    icon: "💚",
    color: "from-pink-500 to-rose-600",
    bgColor: "bg-pink-50",
    textColor: "text-pink-700",
    hasMediaGallery: false,
    activities: [
      "Charity distributions and Zakat",
      "Orphan support and sponsorship programs",
      "Widow and vulnerable assistance",
      "Medical aid and health programs",
      "Educational scholarships",
      "Community awareness campaigns"
    ]
  },
  "finance-investment": {
    name: "Finance and Investment",
    tagline: "Financial Stewardship & Growth",
    description: "The Finance and Investment committee oversees financial management, fundraising, and investment opportunities. We ensure transparent financial operations and sustainable growth for the foundation.",
    icon: "💰",
    color: "from-amber-500 to-orange-600",
    bgColor: "bg-amber-50",
    textColor: "text-amber-700",
    hasMediaGallery: false,
    activities: [
      "Strategic financial planning",
      "Budget management and allocation",
      "Fundraising campaigns and events",
      "Investment oversight and growth",
      "Donation management and tracking",
      "Transparent financial reporting"
    ]
  },
  "security-ethic": {
    name: "Security and Ethics",
    tagline: "Safety & Moral Standards",
    description: "The Security and Ethics committee ensures security within our premises and maintains ethical standards in all operations. We implement safety protocols and uphold the moral principles of the foundation.",
    icon: "🛡️",
    color: "from-purple-500 to-violet-600",
    bgColor: "bg-purple-50",
    textColor: "text-purple-700",
    hasMediaGallery: false,
    activities: [
      "Premises security and monitoring",
      "Safety protocols implementation",
      "Ethical guidelines enforcement",
      "Conflict resolution services",
      "Community standards maintenance",
      "Emergency response procedures"
    ]
  }
};

const Committee = () => {
  const { id } = useParams();
  const [committee, setCommittee] = useState(null);
  const [posts, setPosts] = useState([]);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMediaTab, setActiveMediaTab] = useState("all");
  const [selectedMedia, setSelectedMedia] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const data = committeeData[id];
    if (data) {
      setCommittee(data);
      
      try {
        const postsRes = await axios.get(`${API}/posts?committee=${id}`);
        setPosts(postsRes.data);

        if (data.hasMediaGallery) {
          const mediaRes = await axios.get(`${API}/media?committee=${id}`);
          setMedia(mediaRes.data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "MMM d, yyyy");
    } catch {
      return dateStr;
    }
  };

  const filteredMedia = useMemo(() => 
    activeMediaTab === "all" 
      ? media 
      : media.filter(m => m.media_type === activeMediaTab),
    [media, activeMediaTab]
  );

  const handleDownload = async (mediaItem) => {
    try {
      const response = await axios.get(`${API}/media/${mediaItem.media_id}/download`);
      if (response.data.file_data) {
        const link = document.createElement('a');
        link.href = `data:${response.data.mime_type};base64,${response.data.file_data}`;
        link.download = response.data.file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Download error:", error);
    }
  };

  const getMediaIcon = (type) => {
    switch (type) {
      case "image": return <Image className="w-5 h-5" />;
      case "video": return <Video className="w-5 h-5" />;
      case "audio": return <Music className="w-5 h-5" />;
      case "document": return <FileText className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  const mediaCounts = {
    all: media.length,
    image: media.filter(m => m.media_type === "image").length,
    video: media.filter(m => m.media_type === "video").length,
    audio: media.filter(m => m.media_type === "audio").length,
    document: media.filter(m => m.media_type === "document").length
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-900"></div>
      </div>
    );
  }

  if (!committee) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Card className="p-12 text-center max-w-md">
          <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Committee Not Found</h2>
          <p className="text-gray-600 mb-6">The committee you're looking for doesn't exist.</p>
          <Button asChild className="btn-primary">
            <Link to="/">Go Home</Link>
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="bg-stone-50">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className={`absolute inset-0 bg-gradient-to-br ${committee.color}`}></div>
        <div className="pattern-overlay absolute inset-0"></div>
        <div className="container-custom relative z-10">
          <Link 
            to="/" 
            className="inline-flex items-center text-white/80 hover:text-white mb-8 transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Link>
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-medium mb-6">
              <span className="text-xl">{committee.icon}</span>
              Committee
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight">
              {committee.name}
            </h1>
            <p className="text-xl text-white/90 font-medium mb-6">{committee.tagline}</p>
            <p className="text-lg text-white/80 leading-relaxed">{committee.description}</p>
          </div>
        </div>
      </section>

      {/* Activities Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-primary-600 font-semibold text-sm uppercase tracking-wider mb-3">What We Do</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">Our Activities</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {committee.activities.map((activity, index) => (
              <Card key={index} className="bento-card p-6 group" data-testid={`activity-${index}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl ${committee.bgColor} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                    <CheckCircle className={`w-6 h-6 ${committee.textColor}`} />
                  </div>
                  <p className="font-medium text-gray-700 leading-relaxed">{activity}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Media Gallery - Only for Mosque & Da'wah */}
      {committee.hasMediaGallery && (
        <section className="section-padding">
          <div className="container-custom">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
              <div>
                <p className="text-primary-600 font-semibold text-sm uppercase tracking-wider mb-1">Browse Content</p>
                <h2 className="font-heading text-3xl font-bold text-gray-900 tracking-tight">Media Gallery</h2>
              </div>
              <Link 
                to="/media-gallery" 
                className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-900 font-semibold transition-colors group"
              >
                View Full Gallery 
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <Tabs defaultValue="all" className="w-full" onValueChange={setActiveMediaTab}>
              <TabsList className="bg-stone-100 p-1 rounded-xl mb-8 flex-wrap h-auto">
                <TabsTrigger value="all" className="rounded-lg data-[state=active]:bg-white px-4 py-2" data-testid="media-tab-all">
                  All ({mediaCounts.all})
                </TabsTrigger>
                <TabsTrigger value="image" className="rounded-lg data-[state=active]:bg-white px-4 py-2" data-testid="media-tab-image">
                  <Image className="w-4 h-4 mr-1" /> Images ({mediaCounts.image})
                </TabsTrigger>
                <TabsTrigger value="video" className="rounded-lg data-[state=active]:bg-white px-4 py-2" data-testid="media-tab-video">
                  <Video className="w-4 h-4 mr-1" /> Videos ({mediaCounts.video})
                </TabsTrigger>
                <TabsTrigger value="audio" className="rounded-lg data-[state=active]:bg-white px-4 py-2" data-testid="media-tab-audio">
                  <Music className="w-4 h-4 mr-1" /> Audio ({mediaCounts.audio})
                </TabsTrigger>
                <TabsTrigger value="document" className="rounded-lg data-[state=active]:bg-white px-4 py-2" data-testid="media-tab-document">
                  <FileText className="w-4 h-4 mr-1" /> Docs ({mediaCounts.document})
                </TabsTrigger>
              </TabsList>

              <TabsContent value={activeMediaTab}>
                {filteredMedia.length === 0 ? (
                  <Card className="p-16 text-center bg-white border-dashed rounded-3xl">
                    <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-6">
                      <Image className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">No Media Yet</h3>
                    <p className="text-gray-500">Media content will appear here once uploaded.</p>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredMedia.map((item) => (
                      <Card 
                        key={item.media_id} 
                        className="bento-card overflow-hidden cursor-pointer group"
                        onClick={() => setSelectedMedia(item)}
                        data-testid={`media-${item.media_id}`}
                      >
                        <div className="aspect-[4/3] bg-gray-100 relative overflow-hidden">
                          {item.media_type === "image" && (
                            <img src={item.file_url} alt={item.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                          )}
                          {item.media_type === "video" && (
                            <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                              <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                                <Play className="w-8 h-8 text-white ml-1" />
                              </div>
                            </div>
                          )}
                          {item.media_type === "audio" && (
                            <div className={`w-full h-full bg-gradient-to-br ${committee.color} flex items-center justify-center`}>
                              <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                                <Music className="w-8 h-8 text-white" />
                              </div>
                            </div>
                          )}
                          {item.media_type === "document" && (
                            <div className="w-full h-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                              <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                                <FileText className="w-8 h-8 text-white" />
                              </div>
                            </div>
                          )}
                          
                          {/* Overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                              <span className="text-white text-sm font-medium flex items-center gap-1">
                                <Eye className="w-4 h-4" />
                                View
                              </span>
                              {(item.media_type === "audio" || item.media_type === "video") && (
                                <button 
                                  onClick={(e) => { e.stopPropagation(); handleDownload(item); }}
                                  className="p-2 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-colors"
                                  data-testid={`download-${item.media_id}`}
                                >
                                  <Download className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="p-4">
                          <h3 className="font-semibold text-gray-900 truncate group-hover:text-primary-900 transition-colors">
                            {item.title}
                          </h3>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </section>
      )}

      {/* Posts Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-primary-600 font-semibold text-sm uppercase tracking-wider mb-3">Stay Informed</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">Latest Updates</h2>
          </div>
          
          {posts.length === 0 ? (
            <Card className="p-16 text-center bg-stone-50 border-dashed rounded-3xl">
              <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">No Posts Yet</h3>
              <p className="text-gray-500">Updates from this committee will appear here.</p>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {posts.map((post, index) => (
                <Card 
                  key={post.post_id} 
                  className={`bento-card overflow-hidden ${index === 0 ? 'md:col-span-2' : ''}`}
                  data-testid={`committee-post-${post.post_id}`}
                >
                  {post.image_url && (
                    <div className={`overflow-hidden ${index === 0 ? 'h-72' : 'h-48'}`}>
                      <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        post.category === 'news' ? 'bg-blue-100 text-blue-700' :
                        post.category === 'announcement' ? 'bg-amber-100 text-amber-700' :
                        'bg-emerald-100 text-emerald-700'
                      }`}>
                        {post.category}
                      </span>
                    </div>
                    <Link to={`/post/${post.post_id}`}>
                      <h3 className={`font-heading font-bold text-gray-900 mb-3 hover:text-primary-900 transition-colors ${index === 0 ? 'text-2xl' : 'text-xl'}`}>
                        {post.title}
                      </h3>
                    </Link>
                    <p className="text-gray-600 line-clamp-2 mb-4">{post.content}</p>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                          <Users className="w-4 h-4 text-primary-900" />
                        </div>
                        <span>{post.author_name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDate(post.created_at)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Media Modal */}
      {selectedMedia && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedMedia(null)}
        >
          <div 
            className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <Button 
                variant="ghost" 
                size="icon"
                className="absolute top-4 right-4 z-10 bg-black/50 text-white hover:bg-black/70 rounded-full"
                onClick={() => setSelectedMedia(null)}
              >
                <X className="w-5 h-5" />
              </Button>
              
              <div className="aspect-video bg-gray-900 flex items-center justify-center">
                {selectedMedia.media_type === "image" && (
                  <img src={selectedMedia.file_url} alt={selectedMedia.title} className="max-w-full max-h-full object-contain" />
                )}
                {selectedMedia.media_type === "video" && (
                  <video src={selectedMedia.file_url} controls autoPlay className="max-w-full max-h-full" />
                )}
                {selectedMedia.media_type === "audio" && (
                  <div className="text-center p-12">
                    <div className={`w-24 h-24 rounded-full bg-gradient-to-br ${committee.color} flex items-center justify-center mx-auto mb-6`}>
                      <Music className="w-12 h-12 text-white" />
                    </div>
                    <h3 className="text-white text-xl font-semibold mb-4">{selectedMedia.title}</h3>
                    <audio src={selectedMedia.file_url} controls autoPlay className="w-full max-w-md mx-auto" />
                  </div>
                )}
                {selectedMedia.media_type === "document" && (
                  <div className="text-center p-12">
                    <div className="w-24 h-24 rounded-2xl bg-amber-500 flex items-center justify-center mx-auto mb-6">
                      <FileText className="w-12 h-12 text-white" />
                    </div>
                    <h3 className="text-white text-xl font-semibold mb-2">{selectedMedia.title}</h3>
                    <p className="text-gray-400 mb-6">{selectedMedia.file_name}</p>
                    <Button asChild className="bg-white text-gray-900 hover:bg-gray-100">
                      <a href={selectedMedia.file_url} target="_blank" rel="noopener noreferrer">Open Document</a>
                    </Button>
                  </div>
                )}
              </div>
            </div>
            
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-heading text-xl font-bold text-gray-900 mb-1">{selectedMedia.title}</h2>
                  {selectedMedia.description && (
                    <p className="text-gray-600">{selectedMedia.description}</p>
                  )}
                </div>
                {(selectedMedia.media_type === "audio" || selectedMedia.media_type === "video") && (
                  <Button onClick={() => handleDownload(selectedMedia)} className="btn-primary">
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Committee;
